import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import API from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { ChatText, Car, ArrowBendUpLeft, Check, Checks } from "@phosphor-icons/react";

const STATUS_CLS = { delivered: "text-emerald-700 border-emerald-300", failed: "text-red-600 border-red-300", undelivered: "text-red-600 border-red-300", sent: "text-sky-700 border-sky-300" };
const fmt = (iso) => new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

function Bubble({ m }) {
  const inbound = m.direction === "inbound";
  return (
    <li className={`flex ${inbound ? "justify-start" : "justify-end"}`} data-testid={`job-sms-row-${m.id}`}>
      <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-xs shadow-sm ${inbound ? "bg-muted rounded-bl-sm" : "bg-primary/10 dark:bg-primary/20 rounded-br-sm"} ${inbound && !m.read ? "ring-2 ring-amber-400" : ""}`}
           data-testid={inbound ? "job-sms-inbound" : "job-sms-outbound"}>
        <div className="flex items-center gap-2 text-[10px] text-muted-foreground mb-0.5">
          <span>{inbound ? "Client" : (m.by || "You")} · {fmt(m.created_at)}{!inbound && m.kind ? ` · ${m.kind.replace(/_/g, " ")}` : ""}</span>
          {inbound && !m.read && <Badge className="h-4 px-1.5 text-[9px] bg-amber-500 hover:bg-amber-500 text-white" data-testid="job-sms-new-badge">New</Badge>}
          {inbound && m.read && m.replied_at && <span className="inline-flex items-center gap-0.5 text-emerald-600"><Checks size={12} /> replied</span>}
          {inbound && m.read && !m.replied_at && <span className="inline-flex items-center gap-0.5"><Check size={12} /> seen</span>}
        </div>
        <p className="whitespace-pre-wrap break-words">{m.body}</p>
        {m.error_message && <p className="text-red-600 mt-0.5">{m.error_message}</p>}
        {!inbound && <div className="text-right mt-0.5"><Badge variant="outline" className={`text-[9px] h-4 px-1 ${STATUS_CLS[m.status] || ""}`}>{m.status}</Badge></div>}
      </div>
    </li>
  );
}

export default function JobSmsPanel({ jobId, canSend, canOnTheWay }) {
  const [data, setData] = useState(null);
  const [eta, setEta] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState("");
  const [compose, setCompose] = useState(false);
  const listRef = useRef(null);

  const load = useCallback(async () => {
    try { const { data } = await API.get(`/jobs/${jobId}/sms`); setData(data); } catch { setData({ connected: false, items: [] }); }
  }, [jobId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (data?.unread_replies > 0) API.post(`/jobs/${jobId}/sms/read`).catch(() => {});
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [data, jobId]);
  useEffect(() => {
    if (window.location.hash === "#sms") document.querySelector('[data-testid="job-sms-panel"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [data]);

  if (!data) return null;
  if (!data.connected) {
    if (!canSend) return null;
    return (
      <div className="border border-dashed border-border rounded-md p-4 flex flex-wrap items-center justify-between gap-2" data-testid="job-sms-panel">
        <div className="flex items-center gap-2 text-sm"><ChatText size={16} weight="duotone" className="text-muted-foreground" />
          <span className="text-muted-foreground" data-testid="job-sms-not-connected">Text messaging isn't set up — connect your Twilio account under <Link to="/settings?tab=integrations" className="underline text-primary" data-testid="job-sms-setup-link">Settings → Integrations</Link>.</span>
        </div>
        <span className="text-[11px] text-muted-foreground" data-testid="job-sms-recipient">{data.recipient ? `client mobile ${data.recipient}` : "no mobile number on this job"}</span>
      </div>
    );
  }

  const canText = !!data.recipient && !data.opted_out;
  const act = async (label, fn) => {
    setBusy(label);
    try { const row = await fn(); toast.success(`Text sent to ${row.to}`); setEta(""); setBody(""); setCompose(false); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not send the text"); }
    finally { setBusy(""); }
  };
  const onTheWay = () => act("otw", async () => (await API.post(`/jobs/${jobId}/sms/on-the-way`, { eta_minutes: eta ? Number(eta) : undefined })).data);
  const quick = (key) => act(key, async () => (await API.post(`/jobs/${jobId}/sms/quick-reply`, { key })).data);
  const send = () => body.trim() && act("send", async () => (await API.post(`/jobs/${jobId}/sms/send`, { body })).data);
  const items = [...(data.items || [])].sort((a, b) => (a.created_at > b.created_at ? 1 : -1));
  const unread = data.unread_replies || 0;

  return (
    <div className={`border rounded-md p-4 space-y-3 ${unread ? "border-amber-400" : "border-border"}`} data-testid="job-sms-panel">
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm font-semibold flex items-center gap-2" role="heading" aria-level={3}><ChatText size={16} weight="duotone" /> Texts with the client
          {unread > 0 && <Badge className="bg-amber-500 hover:bg-amber-500 text-white" data-testid="job-sms-unread-badge">{unread} new repl{unread === 1 ? "y" : "ies"}</Badge>}
        </div>
        <span className="text-[11px] text-muted-foreground" data-testid="job-sms-recipient">{data.recipient ? `to ${data.recipient}` : "no mobile number on this job"}{data.opted_out ? " · opted out" : ""}</span>
      </div>

      {items.length > 0 && (
        <ul ref={listRef} className="space-y-2 max-h-72 overflow-y-auto pr-1" data-testid="job-sms-log">
          {items.map((m) => <Bubble key={m.id} m={m} />)}
        </ul>
      )}

      {canOnTheWay && (data.quick_replies || []).length > 0 && (
        <div className="space-y-1" data-testid="job-sms-quick-replies">
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground flex items-center gap-1"><ArrowBendUpLeft size={12} /> One-tap replies</p>
          <div className="flex flex-wrap gap-1.5">
            {data.quick_replies.map((q) => (
              <Button key={q.key} size="sm" variant="outline" className="h-7 rounded-full text-xs" title={q.preview} onClick={() => quick(q.key)} disabled={!!busy || !canText} data-testid={`job-sms-quick-${q.key}`}>
                {busy === q.key ? "Sending…" : q.label}
              </Button>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {canOnTheWay && (
          <>
            <Input type="number" min="1" max="600" placeholder="ETA (min)" value={eta} onChange={(e) => setEta(e.target.value)} className="h-8 w-28 text-xs" data-testid="job-sms-eta-input" />
            <Button size="sm" onClick={onTheWay} disabled={!!busy || !canText} className="gap-1" data-testid="job-sms-on-the-way-btn">
              <Car size={14} /> {busy === "otw" ? "Sending…" : "Send \"On the way\""}
            </Button>
          </>
        )}
        {canSend && !compose && <Button size="sm" variant="ghost" onClick={() => setCompose(true)} disabled={!canText} data-testid="job-sms-compose-btn">Write a message…</Button>}
      </div>
      {canSend && compose && (
        <div className="space-y-2">
          <Textarea rows={2} maxLength={320} autoFocus placeholder="Type a message… (max 320 characters)" value={body} onChange={(e) => setBody(e.target.value)} className="text-sm" data-testid="job-sms-body-input" />
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-muted-foreground">{body.length}/320 · billed to your Twilio account</span>
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" onClick={() => { setCompose(false); setBody(""); }} data-testid="job-sms-cancel-btn">Cancel</Button>
              <Button size="sm" variant="outline" onClick={send} disabled={!!busy || !body.trim() || !canText} data-testid="job-sms-send-btn">{busy === "send" ? "Sending…" : "Send text"}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
